/**
 * Decide quando un rebase UdM può assorbire eventi che il proiettore ha
 * momentaneamente messo in quarantena. È sicuro soltanto per carichi: dopo il
 * cutover la loro UdM deve coincidere esattamente con quella nuova. Scarichi e
 * prenotazioni restano fail-closed perché non possiamo dedurne la quantità.
 */
export interface QuarantinedUomEntry {
  code?: string | null;
  event: {
    kind?: string | null;
    server_id?: string | null;
    server_rev?: number | null;
    rev?: number | null;
    payload?: {
      food_id?: string | null;
      qty?: number | string | null;
      uom?: string | null;
      stock_qty?: number | string | null;
      stock_uom?: string | null;
      reserve_released?: boolean | null;
      lot_closed?: boolean | null;
      lot_state_action?: string | null;
      movement_class?: string | null;
    } | null;
  };
}

function normalize(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

export function rebaseQuarantineBlock(input: {
  events: readonly QuarantinedUomEntry[];
  foodId: string;
  targetUom?: string | null;
}): string | null {
  const relevant = input.events.filter(
    ({ event }) => event.payload?.food_id === input.foodId,
  );
  if (relevant.length === 0) return null;
  if (relevant.some(({ code, event }) => {
    const confirmed = (
      Number.isInteger(event.server_rev)
      && Number(event.server_rev) > 0
    ) || (
      Number.isInteger(event.rev)
      && Number(event.rev) > 0
      && normalize(event.server_id) !== ''
    );
    const qty = event.payload?.stock_qty ?? event.payload?.qty;
    return (
      code !== 'incompatible_uom'
      || event.kind !== 'stock_in'
      || !confirmed
      || typeof qty !== 'number'
      || !Number.isFinite(qty)
      || qty <= 0
      || event.payload?.reserve_released === true
      || Object.prototype.hasOwnProperty.call(event.payload ?? {}, 'lot_closed')
      || normalize(event.payload?.lot_state_action) !== ''
      || event.payload?.movement_class === 'uom_rebase'
    );
  })) {
    return 'Impossibile convertire: sono presenti scarichi o prenotazioni con UdM non coerente.';
  }
  const quarantinedUoms = new Set(
    relevant.map(({ event }) => normalize(
      event.payload?.stock_uom ?? event.payload?.uom,
    )),
  );
  if (quarantinedUoms.has('')) {
    return 'Impossibile convertire: un carico non coerente non indica la sua UdM.';
  }
  if (!input.targetUom) {
    return quarantinedUoms.size === 1
      ? null
      : 'Impossibile convertire: i carichi non coerenti usano più UdM diverse.';
  }
  const target = normalize(input.targetUom);
  if (!target || [...quarantinedUoms].some((uom) => uom !== target)) {
    return 'Impossibile convertire: i carichi non coerenti devono già essere nella nuova UdM scelta.';
  }
  return null;
}

/**
 * Espone alla UI lo stesso criterio fail-closed usato al submit: il percorso
 * di riallineamento resta raggiungibile soltanto quando la quarantena del
 * prodotto contiene esclusivamente carichi in una sola UdM.
 */
export function canOpenUomRebase(
  input: Omit<Parameters<typeof rebaseQuarantineBlock>[0], 'targetUom'>,
): boolean {
  return rebaseQuarantineBlock(input) == null;
}

/** Il comando preserva il valore totale; il costo medio di presentazione è arrotondato. */
export function uomRebaseUnitPrice(value: number | null, qty: number): number | null {
  return value == null ? null : value / qty;
}
